import React, { useState } from "react";
import { Loader2, RefreshCw, Send, X } from "lucide-react";
import { EmptyState } from "./Cards";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AUDIENCE_LABELS = {
  unbooked_leads_48h: "48h unbooked leads",
  magic_dropoffs: "Magic preview drop-offs",
  cold_customers_90d: "90-day cold customers",
};

export const RetargetPanel = ({ queue, audienceFilter, setAudienceFilter, onScan, scanning, authAxios, onRefresh }) => (
  <div className="space-y-5" data-testid="doozyad-retarget-panel">
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-5">
      <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
        <Send size={16} className="text-emerald-600" /> Retargeting agent
      </h3>
      <p className="text-xs text-[#64748B] mb-3">
        Daily 10 AM IST cron scans 3 audiences and queues personalized re-engagement messages for your approval.
        WhatsApp via Twilio · Email for Magic drop-offs only · 30-day cooldown per phone per audience.
      </p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={onScan}
          disabled={scanning}
          className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
          data-testid="doozyad-retarget-scan-btn"
        >
          {scanning ? <><Loader2 size={13} className="animate-spin" /> Scanning all 3 audiences (~1-3 min)…</> : <><RefreshCw size={13} /> Run scan now</>}
        </button>
        <select
          value={audienceFilter}
          onChange={(e) => setAudienceFilter(e.target.value)}
          className="px-3 py-2 text-xs border border-gray-200 rounded-xl bg-white"
          data-testid="doozyad-retarget-audience-filter"
        >
          <option value="">All audiences</option>
          {Object.entries(AUDIENCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
    </div>

    {queue.length === 0 ? (
      <EmptyState icon={Send} title="No pending retargeting messages" subtitle="Run a scan to queue messages, or wait for the daily 10 AM IST cron." />
    ) : (
      <div className="space-y-3">
        {queue.map((row) => (
          <RetargetCard key={row.id} row={row} authAxios={authAxios} onChange={onRefresh} />
        ))}
      </div>
    )}
  </div>
);

const RetargetCard = ({ row, authAxios, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [editedMsg, setEditedMsg] = useState(row.wa_message);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!window.confirm(`Send WhatsApp message to ${row.phone}?`)) return;
    setBusy(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/admin/doozyad/retargeting/${row.id}/approve`, {
        edited_wa_message: editing ? editedMsg : undefined,
      });
      const wa = r.data?.send_results?.whatsapp;
      alert(wa?.status === "ok" ? `Sent · SID: ${wa.sid}` : `Status: ${r.data?.status}`);
      await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Send failed");
    }
    setBusy(false);
  };

  const skip = async () => {
    if (!window.confirm("Skip this retargeting message?")) return;
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/retargeting/${row.id}/skip`);
      await onChange();
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={`doozyad-retarget-${row.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-semibold uppercase tracking-wider">
            {AUDIENCE_LABELS[row.audience] || row.audience}
          </span>
          <div className="text-sm font-bold text-[#1E1B4B] mt-1">{row.name || "Customer"} · {row.phone}</div>
          {row.email && <div className="text-[11px] text-[#64748B]">{row.email}</div>}
        </div>
        <span className="text-[10px] text-[#94A3B8]">{new Date(row.created_at).toLocaleDateString("en-IN")}</span>
      </div>
      {editing ? (
        <textarea
          value={editedMsg}
          onChange={(e) => setEditedMsg(e.target.value)}
          rows={5}
          className="w-full px-3 py-2 text-xs border border-emerald-200 rounded-lg outline-none focus:border-emerald-400"
          data-testid={`doozyad-retarget-edit-${row.id}`}
        />
      ) : (
        <pre className="text-xs text-[#1E1B4B] whitespace-pre-wrap leading-relaxed bg-gray-50 rounded-lg p-3 font-sans">{row.wa_message}</pre>
      )}
      <div className="flex gap-2 mt-3 flex-wrap">
        <button onClick={approve} disabled={busy}
          className="flex-1 min-w-[8rem] px-3 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
          data-testid={`doozyad-retarget-approve-${row.id}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Approve & send WhatsApp
        </button>
        <button onClick={() => setEditing(!editing)} disabled={busy}
          className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200">
          {editing ? "Use original" : "Edit message"}
        </button>
        <button onClick={skip} disabled={busy}
          className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 flex items-center gap-1"
          data-testid={`doozyad-retarget-skip-${row.id}`}>
          <X size={13} /> Skip
        </button>
      </div>
    </div>
  );
};
